/**
 * HPN Quiz Tracker — reusable Klaviyo client-side tracking for multi-step quizzes.
 *
 * Talks only to Klaviyo's public Client API (company_id = public site ID), so no server is needed.
 * The quiz just dispatches bubbling CustomEvents named `<eventPrefix><name>`:
 *   started | resumed | step-viewed {step} | step-answered {step, answers}
 *   email-captured {answers} | consent-captured {email, firstName, phone, consentedAt} | complete {answers, ...}
 * `answers` is a snapshot of every answer so far (string or array values).
 *
 * The profile is keyed by a per-run guest_id (external_id) from the first step, so every step
 * updates the same profile; once the email arrives Klaviyo attaches it to that same profile.
 *
 *   HPNQuizTracker.init({
 *     rootSelector: '[data-my-quiz-root]', quizId: 'nad3', quizName: 'NAD3 Quiz', eventPrefix: 'nad3-quiz:',
 *     steps: { goals: ['goals'], age: ['age'] },            // step -> answer keys it collects
 *     format: function (key, value) { return value; },       // optional label mapping for answers
 *     propPrefix: 'quiz_nad3_', aliases: { q5: 'symptoms' }, // optional profile property naming
 *     universal: { age: 'age' },                             // answers stored under a shared, unprefixed key
 *     extra: function (name, detail, answers) { return {}; },// optional extra props (profile + event)
 *     viewEvents: ['break1'],                                // steps that also send a 'viewed' event
 *     metrics: { started: 'Started NAD3 Quiz' }              // optional Klaviyo metric name overrides
 *   });
 * Public key / list id / server endpoint come from data-klaviyo-public-key, data-klaviyo-list-id and
 * data-list-endpoint on the root, or from config.publicKey / config.listEndpoint (+ config.tenant).
 * Quizzes that emit a different event shape can pass config.normalize(name, detail) -> detail|null and
 * config.trackOnly({ 'event-name': 'Metric Name' }).
 */
(function () {
  'use strict';

  var API = 'https://a.klaviyo.com/client';
  var REVISION = '2026-07-15';
  var CONTACT_KEYS = { email: 1, firstName: 1, phone: 1 };
  
  function snake(key) {
    return key.replace(/([A-Z])/g, function (m) { return '_' + m.toLowerCase(); });
  }

  function clean(object) {
    Object.keys(object).forEach(function (key) {
      var v = object[key];
      if (v == null || v === '' || (Array.isArray(v) && !v.length)) delete object[key];
    });
    return object;
  }

  function init(config) {
    var prefix = config.eventPrefix;
    var steps = config.steps || {};
    var quizName = config.quizName || config.quizId;
    var p = config.propPrefix != null ? config.propPrefix : ('quiz_' + config.quizId + '_');
    var aliases = config.aliases || {};
    var universal = config.universal || {};
    var storeKey = 'hpn_quiz_tracker_' + config.quizId;
    var queueKey = storeKey + '_queue';
    var metrics = Object.assign({
      started: 'Started ' + quizName,
      resumed: 'Resumed ' + quizName,
      'step-answered': 'Answered ' + quizName + ' Step',
      dropped: 'Dropped ' + quizName,
      complete: 'Completed ' + quizName,
      'added-to-cart': 'Added ' + quizName + ' Product to Cart'
    }, config.metrics);

    var mem = { answers: {}, contact: {} };

    function load() {
      try { return JSON.parse(sessionStorage.getItem(storeKey)) || {}; } catch (e) { return {}; }
    }
    function save(patch) {
      var s = Object.assign(load(), patch);
      try { sessionStorage.setItem(storeKey, JSON.stringify(s)); } catch (e) {}
      return s;
    }
    function reset() {
      try { sessionStorage.removeItem(storeKey); } catch (e) {}
      mem = { answers: {}, contact: {} };
    }
    function guestId() {
      var s = load();
      if (!s.guestId) s = save({ guestId: 'guest_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10) });
      return s.guestId;
    }

    function rootOf(event) {
      var t = event.target;
      if (t && t.closest) return t.closest(config.rootSelector);
      // events dispatched on document (e.g. added-to-cart) belong to the page's quiz
      return document.querySelector(config.rootSelector);
    }

    // root attribute wins, config value is the fallback (themes without section settings)
    function setting(root, attr, key) {
      return root.getAttribute(attr) || config[key] || '';
    }

    function identity() {
      return clean({
        external_id: guestId(),
        email: mem.contact.email,
        first_name: mem.contact.firstName,
        phone_number: mem.contact.phone
      });
    }

    function split(answers) {
      var out = {};
      Object.keys(answers || {}).forEach(function (key) {
        if (CONTACT_KEYS[key]) return;
        out[key] = config.format ? config.format(key, answers[key]) : answers[key];
      });
      return out;
    }

    function absorb(answers) {
      if (!answers) return;
      mem.answers = Object.assign(mem.answers, answers);
      ['email', 'firstName', 'phone'].forEach(function (k) { if (answers[k]) mem.contact[k] = answers[k]; });
    }

    // ---- transport (with a small sessionStorage retry queue) ----
    function post(path, publicKey, body) {
      return fetch(API + path + '/?company_id=' + encodeURIComponent(publicKey), {
        method: 'POST',
        keepalive: true,
        headers: { 'Accept': 'application/vnd.api+json', 'Content-Type': 'application/vnd.api+json', 'Revision': REVISION },
        body: JSON.stringify({ data: body })
      }).then(function (res) {
        if (res.ok) return res;
        return res.json().catch(function () { return {}; }).then(function (payload) {
          var err = new Error((payload.errors && payload.errors[0] && payload.errors[0].detail) || ('Klaviyo ' + res.status));
          err.status = res.status;
          throw err;
        });
      });
    }

    function enqueue(job) {
      try {
        var q = JSON.parse(sessionStorage.getItem(queueKey)) || [];
        q.push(job);
        sessionStorage.setItem(queueKey, JSON.stringify(q.slice(-30)));
      } catch (e) {}
    }

    function send(job) {
      return post(job.path, job.key, job.body).catch(function (err) {
        // 4xx is a payload problem (retrying won't help); network/5xx is retried later.
        if (!err.status || err.status >= 500 || err.status === 429) enqueue(job);
        else if (window.console && console.error) console.error('[HPNQuizTracker]', err.message);
        throw err;
      });
    }

    function flush() {
      var q;
      try { q = JSON.parse(sessionStorage.getItem(queueKey)) || []; sessionStorage.removeItem(queueKey); } catch (e) { return; }
      q.forEach(function (job) { send(job).catch(function () {}); });
    }

    // ---- Klaviyo payloads ----
    function profileJob(root, props) {
      var key = setting(root, 'data-klaviyo-public-key', 'publicKey');
      if (!key) return;
      var attrs = identity();
      attrs.properties = clean(props);
      send({ path: '/profiles', key: key, body: { type: 'profile', attributes: attrs } }).catch(function () {});
      syncList(root, attrs);
    }

    // Server-side mirror: keeps the profile updated and puts every lead in the list
    // (public API can't add to a list without marking consent). Runs at every step;
    // list membership is requested until the server confirms it once.
    function syncList(root, attrs) {
      var endpoint = setting(root, 'data-list-endpoint', 'listEndpoint');
      if (!endpoint) return;
      var addToList = !load().listAdded;
      fetch(endpoint, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          tenant: config.tenant,
          external_id: attrs.external_id,
          email: attrs.email,
          first_name: attrs.first_name,
          phone_number: attrs.phone_number,
          properties: attrs.properties,
          add_to_list: addToList
        })
      }).then(function (res) {
        if (res.ok && addToList) save({ listAdded: true });
      }).catch(function () { /* client API call above is the fallback */ });
    }

    function eventJob(root, name, props, uid) {
      var key = setting(root, 'data-klaviyo-public-key', 'publicKey');
      if (!key) return;
      send({
        path: '/events',
        key: key,
        body: {
          type: 'event',
          attributes: {
            time: new Date().toISOString(),
            unique_id: guestId() + '|' + (uid || name) + '|' + Date.now(),
            properties: clean(props),
            metric: { data: { type: 'metric', attributes: { name: name } } },
            // Klaviyo silently drops client events that carry an email; external_id alone attaches
            // them to the same profile (the profile call already linked the email).
            profile: { data: { type: 'profile', attributes: { external_id: guestId() } } }
          }
        }
      }).catch(function () {});
    }

    function scalarDetail(detail) {
      var out = {};
      Object.keys(detail || {}).forEach(function (k) {
        var v = detail[k];
        if (v == null || typeof v === 'object') return;
        out[snake(k)] = v;
      });
      return out;
    }

    function statusProps(status) {
      var s = load();
      var out = {};
      out[p + 'status'] = status;
      out[p + 'guest_id'] = guestId();
      out[p + 'last_step'] = s.lastStep;
      out[p + 'last_step_at'] = s.lastStepAt;
      out[p + 'started_at'] = s.startedAt;
      out[p + 'page_url'] = window.location.href;
      return out;
    }

    function answerProps(answers) {
      var out = {};
      var flat = split(answers);
      Object.keys(flat).forEach(function (k) { out[universal[k] || (p + (aliases[k] || snake(k)))] = flat[k]; });
      return out;
    }

    function track(name, detail, status, root, extraProps) {
      absorb(detail && detail.answers);
      var answers = mem.answers;
      var extra = config.extra ? config.extra(name, detail || {}, answers) || {} : {};
      var step = detail && detail.step;
      var stepAnswers = {};
      if (step && steps[step]) {
        var flat = split(answers);
        steps[step].forEach(function (k) { if (flat[k] != null) stepAnswers[k] = flat[k]; });
      }

      var profile = Object.assign(statusProps(status), answerProps(answers), extraProps || {});
      Object.keys(extra).forEach(function (k) { profile[p + k] = extra[k]; });
      profileJob(root, profile);

      var eventProps = Object.assign(scalarDetail(detail), extra, {
        quiz_id: config.quizId,
        guest_id: guestId(),
        quiz_status: status,
        last_step: load().lastStep,
        step_answers: Object.keys(stepAnswers).length ? stepAnswers : undefined
      });
      if (name === 'complete' || name === 'dropped') eventProps.answers = split(answers);
      eventJob(root, metrics[name] || (quizName + ' ' + name), eventProps);
    }

    // ---- lifecycle ----
    var activeRoot = null;

    function on(name, handler) {
      document.addEventListener(prefix + name, function (event) {
        var root = rootOf(event);
        if (!root) return;
        activeRoot = root;
        try {
          var detail = config.normalize ? config.normalize(name, event.detail || {}) : (event.detail || {});
          if (detail) handler(detail, root);
        } catch (error) {
          if (window.console && console.error) console.error('[HPNQuizTracker] ' + name + ' failed:', error && error.message, error);
        }
      });
    }

    on('started', function (detail, root) {
      // A new run only starts clean when a previous one was recorded. Quizzes that ask for
      // the email first fire email-captured before started, and that guest id must survive.
      if (load().startedAt) reset();
      var now = new Date().toISOString();
      save({ startedAt: detail.startedAt || now, status: 'in_progress' });
      track('started', detail, 'in_progress', root);
    });

    on('resumed', function (detail, root) {
      save({ status: 'in_progress', startedAt: detail.startedAt || load().startedAt });
      track('resumed', detail, 'in_progress', root, (function () {
        var o = {}; o[p + 'resumed_at'] = new Date().toISOString(); return o;
      })());
    });

    on('step-viewed', function (detail, root) {
      if (!detail.step) return;
      var alreadyViewed = load().lastStep === detail.step;
      save({ lastStep: detail.step, lastStepAt: new Date().toISOString(), dropSent: false });
      if (alreadyViewed || !load().startedAt || !(config.viewEvents || []).some(function (s) { return s === detail.step; })) return;
      var extra = config.extra ? config.extra('step-viewed', detail, mem.answers) || {} : {};
      eventJob(root, metrics['step-viewed'] || ('Viewed ' + quizName + ' Step'), Object.assign(scalarDetail(detail), extra, {
        quiz_id: config.quizId,
        guest_id: guestId(),
        step: detail.step
      }), 'viewed|' + detail.step);
    });

    on('step-answered', function (detail, root) {
      // quizzes without a view event report the step the person moved on to
      if (detail.viewedStep) save({ lastStep: detail.viewedStep, lastStepAt: new Date().toISOString(), dropSent: false });
      track('step-answered', detail, 'in_progress', root);
    });

    on('email-captured', function (detail, root) {
      absorb(detail.answers);
      var extraProps = {};
      extraProps[p + 'lead_captured_at'] = new Date().toISOString();
      profileJob(root, Object.assign(statusProps('in_progress'), answerProps(mem.answers), extraProps));
    });

    // Email and SMS opt-ins arrive back to back for the same profile. Two concurrent
    // subscription requests race on Klaviyo's side and one gets dropped, so they are
    // merged into a single request.
    var pendingConsent = null;

    function queueSubscribe(channel, detail, root) {
      if (!pendingConsent) {
        pendingConsent = { channels: [], detail: {}, root: root };
        setTimeout(flushConsent, 0);
      }
      if (pendingConsent.channels.indexOf(channel) === -1) pendingConsent.channels.push(channel);
      Object.assign(pendingConsent.detail, detail);
    }

    function flushConsent() {
      var job = pendingConsent;
      pendingConsent = null;
      if (!job) return;
      var root = job.root;
      var detail = job.detail;
      var channels = job.channels;
      var key = setting(root, 'data-klaviyo-public-key', 'publicKey');
      if (!key) return;
      var listId = root.getAttribute('data-klaviyo-list-id');
      // no consented_at: lists with double opt-in reject it, and Klaviyo stamps the request time itself
      var subscriptions = {};
      channels.forEach(function (channel) { subscriptions[channel] = { marketing: { consent: 'SUBSCRIBED' } }; });
      var profileAttrs = clean({
        email: detail.email,
        phone_number: channels.indexOf('sms') !== -1 ? detail.phone : null,
        first_name: detail.firstName,
        subscriptions: subscriptions
      });
      var body = {
        type: 'subscription',
        attributes: { custom_source: quizName, profile: { data: { type: 'profile', attributes: profileAttrs } } }
      };
      if (listId) body.relationships = { list: { data: { type: 'list', id: listId } } };
      post('/subscriptions', key, body).then(function () {
        channels.forEach(function (channel) {
          eventJob(root, 'Subscribed to ' + quizName + ' ' + (channel === 'sms' ? 'SMS' : 'Email'), {
            consent_source: quizName,
            quiz_id: config.quizId
          });
        });
      }).catch(function (error) {
        if (window.console && console.error) console.error('[HPNQuizTracker] subscription failed:', error.message);
        root.dispatchEvent(new CustomEvent(prefix + 'integration-error', {
          bubbles: true,
          detail: { integration: channels.indexOf('sms') !== -1 ? 'klaviyo_sms' : 'klaviyo_email', message: error.message }
        }));
      });
    }

    on('consent-captured', function (detail, root) {
      if (detail.phone) queueSubscribe('sms', detail, root);
    });

    on('email-consent-captured', function (detail, root) {
      queueSubscribe('email', detail, root);
    });

    on('complete', function (detail, root) {
      var now = detail.completedAt || new Date().toISOString();
      save({ status: 'completed', completedAt: now, lastStep: config.resultStep || 'result', lastStepAt: now });
      var extraProps = {};
      extraProps[p + 'completed_at'] = now;
      track('complete', detail, 'completed', root, extraProps);
    });

    on('added-to-cart', function (detail, root) {
      var extra = config.extra ? config.extra('added-to-cart', detail, mem.answers) || {} : {};
      eventJob(root, metrics['added-to-cart'], Object.assign(scalarDetail(detail), extra, { quiz_id: config.quizId, guest_id: guestId() }));
    });

    // Events that only produce a Klaviyo event (no profile update): { 'event-name': 'Metric Name' }
    Object.keys(config.trackOnly || {}).forEach(function (name) {
      on(name, function (detail, root) {
        var extra = config.extra ? config.extra(name, detail, mem.answers) || {} : {};
        eventJob(root, config.trackOnly[name], Object.assign(scalarDetail(detail), extra, { quiz_id: config.quizId, guest_id: guestId() }));
      });
    });

    // Drop = the page is hidden/unloaded after the quiz started and before it completed.
    // The profile keeps quiz_<id>_status=dropped + last_step until the person answers again.
    function drop() {
      var s = load();
      if (!activeRoot || !s.startedAt || s.status !== 'in_progress' || s.dropSent) return;
      save({ status: 'dropped', dropSent: true, droppedAt: new Date().toISOString() });
      var extraProps = {};
      extraProps[p + 'dropped_at'] = load().droppedAt;
      extraProps[p + 'dropped_step'] = s.lastStep;
      track('dropped', { step: s.lastStep }, 'dropped', activeRoot, extraProps);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') drop();
      else if (load().status === 'dropped') save({ status: 'in_progress' });
    });
    window.addEventListener('pagehide', drop);
    window.addEventListener('online', flush);
    flush();
  }

  window.HPNQuizTracker = { init: init };
})();
